import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import SEOHead from '../common/SEOHead';
import { getGuidesCategorySEO } from '../../utils/seo';
import { useInView } from '../../hooks/useInView';
import { getCategory, getGuidesByCategory, GUIDE_CATEGORIES } from '../../data/guides';

const Reveal: React.FC<{ children: React.ReactNode; delay?: number; className?: string }> = ({
  children,
  delay = 0,
  className = '',
}) => {
  const { ref, inView } = useInView<HTMLDivElement>({ threshold: 0.1 });
  return (
    <div
      ref={ref}
      className={`transition-all duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${
        inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      } ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const GuidesCategory: React.FC = () => {
  const { category } = useParams<{ category: string }>();
  const cat = category ? getCategory(category) : null;

  if (!cat) return <Navigate to="/guides" replace />;

  const guides = getGuidesByCategory(cat.id);
  const otherCategories = GUIDE_CATEGORIES.filter(c => c.id !== cat.id);

  return (
    <div className="bg-cream-50 min-h-screen">
      <SEOHead seoData={getGuidesCategorySEO(cat.id, cat.label, cat.tagline, guides.length)} />

      {/* ===== Hero ===== */}
      <section className="relative bg-cream-50 border-b border-warm-200 overflow-hidden">
        <div className="absolute -top-20 -right-20 w-96 h-96 pointer-events-none">
          <div className="w-full h-full bg-gradient-to-bl from-forest-100/40 to-transparent rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 sm:px-8 py-16 md:py-24">
          <nav className="text-[11px] font-accent italic text-warm-500 mb-8 tracking-wide">
            <Link to="/" className="hover:text-gold-700 transition-colors">Home</Link>
            <span className="mx-2 text-gold-500">•</span>
            <Link to="/guides" className="hover:text-gold-700 transition-colors">Guides</Link>
            <span className="mx-2 text-gold-500">•</span>
            <span className="text-forest-900">{cat.label}</span>
          </nav>

          <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.4em] text-gold-700 font-accent italic mb-4">
            — {cat.eyebrow} —
          </div>
          <h1 className="font-display italic text-[40px] sm:text-[56px] md:text-[68px] text-forest-900 leading-[1.0] tracking-tight mb-7">
            {cat.tagline}
          </h1>
          <p className="text-body-lg md:text-xl text-warm-600 leading-[1.65] max-w-[60ch]">
            {cat.pillarHook}
          </p>

          <div className="mt-10 flex items-center gap-4 flex-wrap">
            <Link to={cat.shopLink} className="btn-secondary text-sm">
              Browse {cat.label.toLowerCase()} products
            </Link>
            <span className="font-accent italic text-[12px] text-warm-500">
              {guides.length} {guides.length === 1 ? 'guide' : 'guides'} in this category
            </span>
          </div>
        </div>
      </section>

      {/* ===== Articles list ===== */}
      <section className="py-20 sm:py-28">
        <div className="max-w-5xl mx-auto px-6 sm:px-8">
          <div className="space-y-5">
            {guides.map((g, idx) => (
              <Reveal key={g.slug} delay={idx * 60}>
                <Link
                  to={`/guides/${cat.id}/${g.slug}`}
                  className="group block bg-cream-100 rounded-2xl border border-warm-200 p-7 sm:p-9 hover:shadow-hover hover:-translate-y-0.5 hover:border-gold-400/50 transition-all duration-500 ease-spring"
                >
                  <div className="flex items-center gap-2 mb-4">
                    <span className="font-accent italic text-[11px] text-warm-500">
                      {g.readMinutes} min read
                    </span>
                    <span className="text-warm-300">·</span>
                    <span className="font-accent italic text-[11px] text-warm-500">
                      {g.wordCount.toLocaleString()} words
                    </span>
                  </div>
                  <h2 className="font-display text-[22px] sm:text-[26px] text-forest-900 tracking-tight leading-tight mb-3 group-hover:text-gold-800 transition-colors">
                    {g.title}
                  </h2>
                  <p className="text-body text-warm-600 leading-[1.7] mb-5">
                    {g.excerpt}
                  </p>
                  <div className="flex items-center gap-2 text-[12px] uppercase tracking-[0.2em] text-forest-700 font-semibold">
                    Read guide
                    <svg className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                    </svg>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Other categories ===== */}
      <section className="bg-cream-100/60 border-t border-warm-200 py-16 sm:py-20">
        <div className="max-w-5xl mx-auto px-6 sm:px-8">
          <div className="text-center mb-10">
            <span className="text-nano font-semibold text-forest-700 uppercase tracking-[0.2em] mb-3 block">
              Other Categories
            </span>
            <h2 className="font-display text-[26px] sm:text-display-sm text-forest-900 tracking-tight">
              Different problem? Different guide.
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {otherCategories.map(c => (
              <Link
                key={c.id}
                to={`/guides/${c.id}`}
                className="group block bg-cream-50 rounded-xl border border-warm-200 p-6 hover:border-gold-400/50 hover:shadow-hover transition-all duration-500 ease-spring"
              >
                <span className="text-[10px] font-semibold text-gold-700 uppercase tracking-[0.18em] block mb-2">
                  {c.eyebrow.split(' • ')[0]}
                </span>
                <h3 className="font-display text-heading text-forest-900 tracking-tight group-hover:text-gold-800 transition-colors">
                  {c.label}
                </h3>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default GuidesCategory;
